import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogOut, Search, Command, ChevronDown, Share2, Menu } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { TID } from "@/lib/testIds";
import { AttentionMenu } from "@/components/common/AttentionMenu";
import { CommandPalette, useCommandPalette } from "@/components/common/CommandPalette";
import { SidebarContent } from "@/components/layout/Sidebar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const TITLES = {
    "/": ["Executive Overview", "Real-time engineering posture across all SAP artifacts"],
    "/workspaces": ["Engineering Workspaces", "Isolated offline projects for SAP security inspection"],
    "/compare": ["Workspace Compare", "Diff two engineering workspaces — KPIs and finding categories"],
    "/authorization": ["Authorization Engineering", "ABAP AUTHORITY-CHECK analysis and weakness detection"],
    "/code-review": ["Code Review · RICEFW Clearance", "Authorization-centric ABAP review and transport clearance verdicts"],
    "/su24": ["SU24 Engineering", "Proposal consistency and obsolescence review"],
    "/odata": ["OData Engineering", "Gateway service metadata and authorization validation"],
    "/graph": ["Knowledge Graph Explorer", "Visual reasoning over the authorization perimeter"],
    "/findings": ["Engineering Findings", "All detections across engines and workspaces"],
    "/recommendations": ["Recommendation Center", "Explainable engineering remediations awaiting review"],
    "/risk": ["Risk Dashboard", "Aggregated risk signals across workspaces"],
    "/compliance": ["Compliance Dashboard", "SOX, GDPR, SoD readiness signals"],
    "/clean-core": ["SAP Clean Core Readiness", "Deviation surface for clean-core adoption"],
    "/reports": ["Executive Reports", "Generate PDF / Word / Excel / PowerPoint artifacts"],
    "/admin": ["Administration", "Users, roles and operational controls"],
    "/settings": ["Settings", "Workspace and platform preferences"],
    "/share": ["Share Hub", "Generate branded, masked links to distribute UTSAVA Genesis"],
    "/docs": ["Documentation", "Platform guide · modules, quick-start and principles"],
    "/about": ["About UTSAVA", "Innovation lead and platform credits"],
    "/utsav": ["Utsav — AI Agent", "Ask, simulate and draft by text or voice"],
    "/roles": ["Sentinel Forge", "Mass PFCG role engineering · field-verified SoD · Fiori catalogs"],
    "/instant-analysis": ["Instant Analysis", "Zero-setup lookup of any T-code, Fiori app or role combination"],
    "/governance/map": ["Global Compliance Map", "Governance frameworks cross-referenced against your landscape"],
    "/governance/mitigation-library": ["Mitigation Control Library", "Preventive, detective and corrective SAP GRC controls"],
    "/admin/ruleset": ["SoD Ruleset Configuration", "Functions, risks and critical activities down to field level"],
    "/admin/digest": ["Weekly Posture Digest", "Scheduled offline bundle of everything that moved"],
};

/** Breadcrumb eyebrow — readable module names instead of raw path segments. */
const EYEBROWS = {
    "/": "Overview",
    "/governance/map": "Governance · Compliance Map",
    "/governance/mitigation-library": "Governance · Mitigation Library",
    "/admin/ruleset": "Governance · Ruleset",
    "/admin/digest": "Governance · Posture Digest",
    "/code-review": "Engine · Code Review",
    "/roles": "Role Engineering · Sentinel Forge",
    "/instant-analysis": "Role Engineering · Instant Analysis",
    "/utsav": "Operations · Utsav Agent",
};

export default function Topbar() {
    const { user, logout } = useAuth();
    const loc = useLocation();
    const nav = useNavigate();
    // pick title by best-prefix match
    const segs = loc.pathname.split("/").filter(Boolean);
    let key = loc.pathname;
    if (segs[0] === "workspaces" && segs.length > 1) {
        key = "/workspaces";
    }
    const [title, subtitle] = TITLES[key] || ["UTSAVA Genesis", "Enterprise Offline SAP Security Engineering"];
    const eyebrow = EYEBROWS[key] || key.replace(/^\//, "").replace(/\//g, " · ") || "overview";
    const [paletteOpen, setPaletteOpen] = useCommandPalette();
    const [navOpen, setNavOpen] = useState(false);

    // Per-route browser tab title — security architects routinely keep several
    // modules open side by side, and identical tabs make that unworkable.
    useEffect(() => {
        document.title = TITLES[key] ? `${title} · UTSAVA Genesis` : "UTSAVA Genesis";
    }, [key, title]);

    const initials = (user?.name || user?.email || "U")
        .split(" ")
        .map((s) => s[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();

    return (
        <header
            data-testid={TID.topbar}
            className="sticky top-0 z-20 border-b border-white/5 border-t border-t-white/10 bg-[#0A0A14]/65 backdrop-blur-2xl"
        >
            <div className="flex items-center gap-4 sm:gap-6 px-4 sm:px-6 py-4">
                <Sheet open={navOpen} onOpenChange={setNavOpen}>
                    <SheetTrigger asChild>
                        <button
                            data-testid="mobile-nav-trigger"
                            className="md:hidden p-2 panel shrink-0 hover:border-cyan-500/30 transition-colors"
                            aria-label="Open navigation"
                        >
                            <Menu size={16} className="text-slate-300" />
                        </button>
                    </SheetTrigger>
                    <SheetContent
                        side="left"
                        data-testid="mobile-nav-drawer"
                        className="w-[280px] p-0 bg-[var(--bg-base)] border-r border-white/10 overflow-y-auto flex flex-col"
                    >
                        <SidebarContent onNavigate={() => setNavOpen(false)} />
                    </SheetContent>
                </Sheet>

                <div className="flex-1 min-w-0">
                    <div className="label-eyebrow mb-1 hidden sm:block" data-testid="topbar-eyebrow">Module · {eyebrow}</div>
                    <h1 className="font-display text-base sm:text-[22px] lg:text-2xl tracking-tight text-white line-clamp-2 sm:truncate">
                        {title}
                    </h1>
                    <p className="hidden sm:block text-xs text-slate-500 mt-0.5 truncate">{subtitle}</p>
                </div>

                <button
                    data-testid="mobile-search-trigger"
                    onClick={() => setPaletteOpen(true)}
                    className="lg:hidden p-2 panel shrink-0 hover:border-cyan-500/30 transition-colors"
                    aria-label="Open command palette"
                    title="Search"
                >
                    <Search size={16} className="text-slate-300" />
                </button>

                <button
                    data-testid="global-search-trigger"
                    onClick={() => setPaletteOpen(true)}
                    className="hidden lg:flex items-center gap-2 px-3 py-2 panel min-w-[280px] text-left hover:border-cyan-500/30 transition-colors group"
                    aria-label="Open command palette"
                >
                    <Search size={14} className="text-slate-500 group-hover:text-cyan-400 transition-colors" />
                    <span className="flex-1 text-sm text-slate-400 group-hover:text-slate-200 transition-colors truncate">
                        Search modules, workspaces, roles…
                    </span>
                    <kbd className="font-mono text-[10px] text-slate-500 border border-slate-700 px-1.5 py-0.5 rounded-sm flex items-center gap-1">
                        <Command size={10} /> K
                    </kbd>
                </button>

                <button
                    data-testid="topbar-share-button"
                    onClick={() => nav("/share")}
                    className="relative hidden lg:block p-2 panel hover:border-cyan-500/30 transition-colors"
                    aria-label="Share"
                    title="Share Hub"
                >
                    <Share2 size={16} className="text-slate-400" />
                </button>

                <AttentionMenu />

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            data-testid={TID.userMenu}
                            className="flex items-center gap-2 lg:gap-3 panel px-2 lg:px-3 py-1.5 hover:border-cyan-500/30 transition-colors shrink-0"
                        >
                            <div className="w-8 h-8 rounded-sm bg-gradient-to-br from-cyan-400/80 to-cyan-600/80 flex items-center justify-center text-xs font-display text-slate-950 font-semibold">
                                {initials}
                            </div>
                            <div className="text-left hidden lg:block">
                                <div className="text-xs text-white leading-tight">{user?.name || "Architect"}</div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-cyan-400/80">
                                    {user?.is_super_admin ? "Owner" : (user?.roles?.[0] || "viewer")}
                                </div>
                            </div>
                            <ChevronDown size={14} className="text-slate-500" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[var(--bg-elevated)] border-white/10 text-slate-200 min-w-[200px]">
                        <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                            Signed in as
                        </DropdownMenuLabel>
                        <DropdownMenuItem className="text-xs text-slate-300 focus:bg-cyan-500/10">{user?.email}</DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-white/5" />
                        <DropdownMenuItem onClick={() => nav("/settings")} className="text-xs focus:bg-cyan-500/10">
                            Settings
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => nav("/about")} className="text-xs focus:bg-cyan-500/10">
                            About UTSAVA
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-white/5" />
                        <DropdownMenuItem
                            data-testid={TID.logout}
                            onClick={logout}
                            className="text-xs text-red-400 focus:bg-red-500/10 focus:text-red-300"
                        >
                            <LogOut size={12} className="mr-2" /> Sign out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            <div className="divider-glow" />
            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        </header>
    );
}
